import { useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, RefreshCw, QrCode, ExternalLink, X } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/kit";

/**
 * Red warning banner shown when the user's Fonnte account is linked but the
 * underlying WhatsApp device is offline (status "disconnect").
 *
 * Props:
 *   device: { device, name, package, status, connected }
 *   onReconnected?: callback fired when the QR poll confirms reconnection
 *   onRefresh?: callback to re-fetch /wa/account from the parent
 */
export default function DeviceDisconnectWarning({ device, onReconnected, onRefresh }) {
  const [qr, setQr] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  if (!device || device.connected !== false) return null;

  const fetchQr = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/wa/refresh-qr");
      if (data.connected) {
        toast.success("Device sudah terhubung kembali!");
        setQr(null);
        onReconnected?.();
      } else if (data.qr) {
        setQr(data.qr);
      } else {
        toast.error("Tidak bisa mengambil QR. Coba reconnect lewat dashboard Fonnte.");
      }
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  };

  const refreshStatus = async () => {
    setRefreshing(true);
    try { await onRefresh?.(); }
    finally { setRefreshing(false); }
  };

  return (
    <div className="rounded-2xl border-2 border-rose-300 bg-rose-50 overflow-hidden" data-testid="device-disconnect-warning">
      <div className="px-5 py-4 flex flex-wrap items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-rose-100 grid place-items-center shrink-0 animate-pulse">
          <AlertTriangle className="w-5 h-5 text-rose-600" />
        </div>
        <div className="flex-1 min-w-[260px]">
          <p className="font-display font-bold text-rose-900 text-base">
            Perangkat WhatsApp kamu sedang <span className="underline">disconnect</span>
          </p>
          <p className="text-sm text-rose-800 mt-1">
            Nomor <b>{device.device || "—"}</b> ({device.name || "Akun Fonnte"}) tidak terhubung ke
            WhatsApp Web. <b>Semua WA Blast & Cek WA akan gagal</b> sampai kamu scan ulang QR-nya.
          </p>
          <p className="text-xs text-rose-700 mt-1.5">
            Status: {device.status || "disconnect"}{device.package ? ` · Paket: ${device.package}` : ""}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button data-testid="device-disconnect-refresh" size="sm" variant="outline" loading={refreshing}
            onClick={refreshStatus}>
            <RefreshCw className="w-3.5 h-3.5" /> Cek ulang status
          </Button>
          <Button data-testid="device-disconnect-scan" size="sm" loading={loading}
            onClick={fetchQr} className="bg-rose-500 hover:bg-rose-600 text-white">
            <QrCode className="w-3.5 h-3.5" /> Scan ulang QR
          </Button>
          <a href="https://md.fonnte.com" target="_blank" rel="noreferrer"
            className="text-xs font-semibold text-rose-700 underline inline-flex items-center gap-1">
            Buka Fonnte <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </div>

      {qr && (
        <div className="px-5 pb-5 pt-2 border-t border-rose-200 bg-white" data-testid="device-disconnect-qr">
          <div className="flex flex-wrap items-start gap-4">
            <img src={qr} alt="QR WhatsApp Fonnte" className="w-48 h-48 rounded-xl border border-rose-200 bg-white p-2" />
            <div className="flex-1 min-w-[200px] text-sm text-slate-700 space-y-2">
              <p className="font-semibold text-slate-800">Cara scan:</p>
              <ol className="list-decimal pl-5 space-y-1 text-slate-600">
                <li>Buka WhatsApp di HP → menu <b>Setelan</b> → <b>Perangkat tertaut</b>.</li>
                <li>Ketuk <b>Tautkan perangkat</b> lalu scan QR di sebelah kiri.</li>
                <li>Tunggu 5–10 detik, klik <b>Cek ulang status</b> di atas.</li>
              </ol>
              <button onClick={() => setQr(null)} className="text-xs text-rose-600 font-semibold inline-flex items-center gap-1 hover:underline">
                <X className="w-3 h-3" /> Tutup QR
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
